import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ExternalLink, Truck } from 'lucide-react';
import { format } from 'date-fns';

const COURIERS = [
  { value: 'dpd',        label: 'DPD' },
  { value: 'ups',        label: 'UPS' },
  { value: 'royal_mail', label: 'Royal Mail' },
  { value: 'dhl',        label: 'DHL' },
  { value: 'fedex',      label: 'FedEx' },
  { value: 'parcelforce',label: 'Parcelforce' },
  { value: 'other',      label: 'Other' },
];

const inputCls = "w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-3 focus:outline-none focus:border-primary/60 placeholder:text-white/20";

export default function OrderShipmentPanel({ orderId, onSaved }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ courier: 'royal_mail', trackingNumber: '', itemsDescription: '', dispatchedDate: '' });

  const { data: shipments = [], isLoading } = useQuery({
    queryKey: ['order-shipments', orderId],
    queryFn: () => base44.entities.OrderShipment.filter({ order_id: orderId }, '-created_date'),
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.trackingNumber.trim()) return;
    setSaving(true);
    await base44.functions.invoke('adminAddShipment', { orderId, ...form });
    await queryClient.invalidateQueries({ queryKey: ['order-shipments', orderId] });
    setAdding(false);
    setForm({ courier: 'royal_mail', trackingNumber: '', itemsDescription: '', dispatchedDate: '' });
    onSaved?.();
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
      ) : shipments.length === 0 && !adding ? (
        <p className="text-white/20 text-xs text-center py-2">No shipments yet</p>
      ) : (
        <div className="space-y-2">
          {shipments.map(s => (
            <div key={s.id} className="bg-[#111115] border border-white/5 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-xs font-bold">{COURIERS.find(c => c.value === s.courier)?.label || s.courier}</span>
                <span className="text-white/20 text-[10px]">{s.dispatched_date}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-white font-mono text-xs">{s.tracking_number}</span>
                {s.tracking_url && (
                  <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 transition-colors">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {s.items_description && <p className="text-white/30 text-[10px] mt-1">{s.items_description}</p>}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <form onSubmit={handleSave} className="space-y-2 border border-white/10 p-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">COURIER</p>
              <select value={form.courier} onChange={e => setForm({ ...form, courier: e.target.value })}
                className="w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-2 focus:outline-none focus:border-primary/60">
                {COURIERS.map(c => <option key={c.value} value={c.value} className="bg-[#111115]">{c.label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">TRACKING NO.</p>
              <input value={form.trackingNumber} onChange={e => setForm({ ...form, trackingNumber: e.target.value })}
                placeholder="e.g. JD000000000" className={inputCls} />
            </div>
          </div>
          <div>
            <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">ITEMS (OPTIONAL)</p>
            <input value={form.itemsDescription} onChange={e => setForm({ ...form, itemsDescription: e.target.value })}
              placeholder="e.g. Steering wheel + pedals" className={inputCls} />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving || !form.trackingNumber.trim()}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />} SAVE SHIPMENT
            </button>
            <button type="button" onClick={() => setAdding(false)} className="text-white/30 text-[10px] font-bold tracking-widest px-3 py-1.5 hover:text-white transition-colors">
              CANCEL
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 text-white/40 hover:text-white text-[10px] font-bold tracking-widest transition-colors">
          <Plus className="w-3.5 h-3.5" /> ADD SHIPMENT
        </button>
      )}
    </div>
  );
}